// src/services/securityService.ts
import { getDeviceUsers, getUserDevices } from "@/api/security";
import type {
  DeviceUser,
  UserDevice,
  SecurityQueryParams,
} from "@/types/security";
import { fingerprintService } from "./fingerprint";

/** 安全中心服务：风控相关功能 */
class SecurityService {
  private currentDeviceHashId: string | null = null;

  /** 初始化当前设备的指纹 hash */
  initCurrentDeviceHash() {
    if (!fingerprintService.isFingerPrintInitialized()) {
      return;
    }

    const fingerprint = fingerprintService.getCurrentFingerprintInfo();
    if (fingerprint?.visitorId) {
      this.currentDeviceHashId = fingerprint.visitorId.split(".")[0];
    }
  }

  /** 检查是否为当前设备 */
  isCurrentDevice(deviceFingerprint: string): boolean {
    if (!this.currentDeviceHashId) {
      this.initCurrentDeviceHash();
    }

    const deviceHashId = deviceFingerprint.split(".")[0];
    return this.currentDeviceHashId === deviceHashId;
  }

  /** 获取当前设备登录用户列表 */
  async getCurrentDeviceUsers(
    params?: SecurityQueryParams,
  ): Promise<DeviceUser[]> {
    try {
      // 获取当前设备指纹
      const fingerprint = await fingerprintService.getFingerprint();
      return await getDeviceUsers(fingerprint, params);
    } catch (error) {
      console.error("获取当前设备用户列表失败:", error);
      throw error;
    }
  }

  /** 获取用户登录过的设备列表 */
  async getUserDevices(
    userId: string,
    params?: SecurityQueryParams,
  ): Promise<UserDevice[]> {
    try {
      return await getUserDevices(userId, params);
    } catch (error) {
      console.error("获取用户设备列表失败:", error);
      throw error;
    }
  }

  /** 获取设备平台信息 */
  getDevicePlatform(fingerprint: string): string {
    try {
      // 设备指纹格式: {hash}.{confidence}.{deviceNameBase64}
      const parts = fingerprint.split(".");

      if (parts.length !== 3) {
        return "未知设备";
      }

      // 解码设备名称 (base64)
      try {
        const decodedName = decodeURIComponent(atob(parts[2]));

        // 检查是否为特定格式的 Android 版本
        if (/^1\d$/.test(decodedName)) {
          return `Android ${decodedName}`;
        }

        return this.formatDeviceName(decodedName);
      } catch (e) {
        console.error("解码设备名称失败:", e);
        return "未知设备";
      }
    } catch (error) {
      console.error("获取设备平台失败:", error);
      return "未知设备";
    }
  }

  /** 格式化设备名称为友好显示 */
  formatDeviceName(deviceName: string): string {
    // 如果已经是 "Android <版本号>" 格式，则直接返回
    if (/^Android \d{2}$/.test(deviceName)) {
      return deviceName;
    }
    const lowerName = deviceName.toLowerCase();

    if (lowerName.includes("iphone")) return "iPhone";
    if (lowerName.includes("ipad")) return "iPad";
    if (lowerName.includes("mac") || lowerName.includes("macbook"))
      return "Mac";
    if (lowerName.includes("android")) return "Android";
    if (lowerName.includes("windows")) return "Windows";
    if (lowerName.includes("linux")) return "Linux";
    if (lowerName.includes("pc")) return "PC";

    return deviceName;
  }

  /** 获取设备图标类型 */
  getDeviceIconType(deviceName: string): string {
    const lowerName = deviceName.toLowerCase();

    if (lowerName.startsWith("android")) return "mobile";
    if (lowerName.includes("iphone") || lowerName.includes("ios"))
      return "mobile";
    if (lowerName.includes("ipad")) return "tablet";
    if (lowerName.includes("mac") || lowerName.includes("macbook"))
      return "desktop";
    if (lowerName.includes("windows") || lowerName.includes("pc"))
      return "desktop";
    if (lowerName.includes("linux")) return "server";

    return "desktop";
  }

  /** 计算相对时间（如：3天前/1小时前） */
  getRelativeTime(dateString: string | null): string {
    if (!dateString) return "未知";

    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();

      // 转换为秒
      const diffSec = Math.floor(diffMs / 1000);

      // 小于1分钟
      if (diffSec < 60) {
        return "刚刚";
      }

      // 分钟
      const diffMin = Math.floor(diffSec / 60);
      if (diffMin < 60) {
        return `${diffMin}分钟前`;
      }

      // 小时
      const diffHour = Math.floor(diffMin / 60);
      if (diffHour < 24) {
        return `${diffHour}小时前`;
      }

      // 天
      const diffDay = Math.floor(diffHour / 24);
      if (diffDay < 30) {
        return `${diffDay}天前`;
      }

      // 月
      const diffMonth = Math.floor(diffDay / 30);
      if (diffMonth < 12) {
        return `${diffMonth}个月前`;
      }

      // 年
      const diffYear = Math.floor(diffMonth / 12);
      return `${diffYear}年前`;
    } catch {
      return "未知时间";
    }
  }

  /** 检查设备是否属于当前用户 */
  isUserOwnDevice(
    device: UserDevice,
    currentUserId: string,
    currentUserName: string,
  ): boolean {
    // 首先检查是否为当前设备
    if (this.isCurrentDevice(device.Fingerprint)) return true;

    // 检查设备的所有者是否匹配当前用户
    if (device.OwnerID === currentUserId) return true;

    // 如果所有者名称与当前用户匹配
    if (
      device.ownerName &&
      device.ownerName !== "No Owner" &&
      device.ownerName === currentUserName
    )
      return true;

    return false;
  }

  /** 格式化设备显示名称 */
  formatDeviceDisplayName(
    device: UserDevice,
    currentUserId: string,
    currentUserName: string,
  ): string {
    // 获取设备平台
    const platform = this.getDevicePlatform(device.Fingerprint);

    // 如果设备没有所有者
    if (!device.OwnerID || device.ownerName === "No Owner") {
      return `${platform} 设备`;
    }

    // 如果是当前用户的设备
    if (this.isUserOwnDevice(device, currentUserId, currentUserName)) {
      return `我的 ${platform}`;
    }

    // 其他用户的设备
    return `${device.ownerName}的 ${platform}`;
  }
}

export const securityService = new SecurityService();
